import {
  Activity,
  AtSign,
  Ban,
  BookOpen,
  Bot,
  Boxes,
  Brain,
  Building2,
  Calculator,
  CalendarCheck2,
  CalendarDays,
  ChartColumn,
  CreditCard,
  FileArchive,
  FileCheck,
  FileMinus,
  FileSignature,
  FileText,
  FolderKanban,
  IdCard,
  Images,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  LifeBuoy,
  ListTodo,
  Mail,
  Package,
  PenTool,
  PieChart,
  PhoneCall,
  Radar,
  ShieldCheck,
  ReceiptText,
  Send,
  Settings2,
  Siren,
  Target,
  Timer,
  UserPlus,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { SETTINGS_PERMS } from "./sections/settings/perms";
import { PROSPECTS, STEPS, stepPath } from "./sections/prospecting/steps";
import type { BadgeKey } from "../lib/shellSignals";

/**
 * The sidebar registry - destinations under five headings.
 *
 * The headings do most of the grouping, so almost nothing needs a sub-menu.
 * Sales is the exception: its six document kinds are separate pages with
 * separate numbering and separate permissions to grow into, and they were
 * reachable from the rail before. They stay reachable from the rail, nested
 * under the section and expanded whenever the route is inside it.
 *
 * Every item carries the platform permission key that guards it (see
 * apps/api/src/lib/permissions.ts). The same key gates the route in
 * `CRM_ROUTES`, so a link and the page behind it can never disagree.
 *
 * A key may also be a LIST, meaning any one of them. Only Settings needs it:
 * its page is five independently gated tabs, so no single key is the honest
 * answer to "who may open this". See `sections/settings/perms.ts`; the check is
 * `userCanPlatformAny`.
 */

/**
 * Live counters the shell can hang off a nav item.
 *
 * Aliased rather than re-listed: the payload decides which counters exist (the
 * server assembles them in `crm-dashboard/shell/collect.ts`), and this file only
 * decides which ones get drawn. Two hand-kept copies of the same six names is
 * how a badge ends up wired to a key nothing ever sends.
 */
export type NavBadge = BadgeKey;

/** One permission key, or a list meaning any one of them. */
export type NavPerm = string | string[];

export interface CrmNavItem {
  /** Where the link goes. */
  path: string;
  labelKey: string;
  icon: LucideIcon;
  perm: NavPerm;
  /**
   * Route prefixes that also light this item up. Needed where the link points
   * at a sub-page (`/marketing/campaigns`) but the whole section belongs to it,
   * and where a detail route sits outside the link's own prefix.
   */
  match?: string[];
  /**
   * Only an exact match of `path` itself counts - used by the dashboard link.
   * `match` prefixes still count on top of it, which is how a row whose link is
   * a section root lights on most of the section but NOT on a sub-page that has
   * a row of its own (Kliendiotsing and its follow-up row below).
   */
  exact?: boolean;
  badge?: NavBadge;
  /**
   * Other names the command palette finds this page by, as catalogue keys.
   * For a page known by two names at once: a Kliendiotsing tab is listed under
   * the name its page had before it became a tab, and the name on the tab
   * strip has to find it too. Never drawn; the sidebar shows `labelKey` only.
   */
  aliasKeys?: string[];
  /** Sub-pages, shown indented under the item while the route is inside it.
   *  A child inherits the parent's permission - nesting a link must never
   *  widen who can reach it. */
  children?: CrmNavChild[];
}

export interface CrmNavChild {
  path: string;
  labelKey: string;
  icon: LucideIcon;
}

/** The sales document kinds. Their own pages, their own numbering; the section
 *  root redirects to the first of them. */
export const SALES_CHILDREN: CrmNavChild[] = [
  { path: "/sales/invoices", labelKey: "sales.invoices.title", icon: FileText },
  { path: "/sales/estimates", labelKey: "sales.estimates.title", icon: Calculator },
  { path: "/sales/proposals", labelKey: "sales.proposals.title", icon: FileCheck },
  { path: "/sales/payments", labelKey: "sales.payments.title", icon: CreditCard },
  { path: "/sales/credit-notes", labelKey: "sales.creditNotes.title", icon: FileMinus },
  { path: "/sales/items", labelKey: "sales.items.title", icon: Package },
  // Not a document kind: what the card studio on the platform's own sites sold, with the
  // print files. Under Sales because every paid one is an invoice here too.
  { path: "/sales/card-orders", labelKey: "sales.cardOrders.title", icon: IdCard },
  // The same for the logo studio: its orders with the ZIP of the logo's files.
  { path: "/sales/logo-orders", labelKey: "sales.logoOrders.title", icon: PenTool },
];

export interface CrmNavGroup {
  titleKey: string;
  items: CrmNavItem[];
}

/**
 * What opens the Calls section, since the telephony key was split in three
 * (§3.8).
 *
 * Any-of, and note which two: being on the phone, or being entitled to the whole
 * log. `manage:crm.calls` is deliberately NOT here. That key is the desk's
 * configuration - credentials, hours, the notice - and its door is Settings ->
 * Phone. Somebody given it to paste a Twilio token in has no call list to look
 * at, and putting the section in their sidebar would offer them one that is
 * empty by design and reads as broken.
 *
 * Nobody loses the section on the day of the split: `backfillPermissionFrom`
 * hands every existing holder of the old key all three of the new ones.
 */
const CALLS_PERMS = ["use:crm.calls", "read:crm.calls.recordings"];

/** Every Kliendiotsing tab but the follow-ups, whose own row lights instead. */
const PROSPECT_ROW_MATCH = STEPS.filter((s) => s.key !== "follow-up").map((s) => stepPath(s.key));

export const CRM_NAV: CrmNavGroup[] = [
  {
    titleKey: "nav.group.desk",
    items: [
      { path: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, perm: "view:crm", exact: true },
      // Beside the overview rather than under Work, because the month it draws
      // is not one section's: tasks, milestones, projects, invoices and planned
      // next steps all land on it, and each of those already answers to its own
      // key on the server (see modules/crm-calendar/routes.ts).
      { path: "/calendar", labelKey: "nav.calendar", icon: CalendarDays, perm: "view:crm" },
      // The handbook. On the desk rather than under Tools, because it is not a
      // tool that produces anything - it is the reference somebody opens WHILE
      // doing the work on every other screen, and it answers to the widest CRM
      // key there is for exactly that reason.
      { path: "/guides", labelKey: "nav.guides", icon: BookOpen, perm: "view:crm" },
    ],
  },
  {
    // The funnel, in the order it is worked: find the companies, look at their
    // sites, pick the leads, write to them, read what comes back, take the call,
    // and keep the list of people who told us to stop.
    titleKey: "nav.group.marketing",
    items: [
      // `exact`, so it lights only on `/marketing` itself. Without it the
      // overview would stay highlighted on every page under the section, and
      // two items in one group would look selected at once.
      { path: "/marketing", labelKey: "nav.marketingOverview", icon: ChartColumn, perm: "view:crm", exact: true },
      // Automation and the opt-out list are NOT rows of their own. Both are
      // pages about the sending as a whole rather than about one letter, both
      // are opened rarely, and both already have a button in this page's head.
      // A rare page that owns a permanent row costs every reader of this group
      // a line to scan; `match` keeps this row lit while the reader is on one
      // of them, so the sidebar never goes blank under them.
      {
        path: "/marketing/campaigns",
        labelKey: "nav.campaigns",
        icon: Send,
        perm: "view:crm",
        match: ["/marketing/automation", "/marketing/suppression"],
      },
      { path: "/marketing/templates", labelKey: "nav.mailTemplates", icon: LayoutTemplate, perm: "view:crm" },
      // The addresses themselves, and the ones we deliberately do not
      // touch. After the letters because it answers "who did that reach"
      // rather than "what are we sending".
      { path: "/marketing/contacts", labelKey: "nav.marketingContacts", icon: AtSign, perm: "view:crm" },
      // The register stays a row of its own, right above the prospect page:
      // it is the find step's tool AND a destination (the company dossier at
      // `/ariregister/:regCode`, linked from a dozen places), not a list step.
      { path: "/ariregister", labelKey: "nav.ariregister", icon: Building2, perm: "view:ariregister" },
      // Kliendiotsing: the audits desk, the scanner, its leads, the brain and
      // the follow-ups, as the tabs of one page in the order the work happens
      // (`sections/prospecting/steps.ts`); they were five rows here. Each step
      // keeps its own view and toolbar, with no shared filter bar.
      //
      // `exact` plus `match` rather than a plain prefix: the row lights on
      // every tab EXCEPT the follow-ups, which have a row of their own below,
      // so no screen shows two selected rows in this group.
      { path: PROSPECTS, labelKey: "nav.prospecting", icon: Radar, perm: "view:ariregister", exact: true, match: PROSPECT_ROW_MATCH },
      // The follow-up tab keeps a row of its own for its badge. The badge
      // counts only what is already late, and the rail draws a badge on the row
      // it links: hung on the Kliendiotsing row, a red number would open the
      // call queue instead of the late promises it is counting. The alias is
      // the tab's own name, which reads the same today but is its own key.
      { path: stepPath("follow-up"), labelKey: "nav.brainDue", aliasKeys: ["prospecting.tabs.followUp"], icon: CalendarCheck2, perm: "view:ariregister", badge: "brainDue" },
      { path: "/email", labelKey: "nav.email", icon: Mail, perm: "view:crm", badge: "mailUnread" },
      { path: "/calls", labelKey: "nav.calls", icon: PhoneCall, perm: CALLS_PERMS, badge: "callsMissed" },
      // Right under the call log, because it is a view of the same calls: the
      // ones the AI placed, with what came of each.
      { path: "/ai-calls", labelKey: "nav.aiCalls", icon: Bot, perm: "use:crm.calls" },
    ],
  },
  {
    titleKey: "nav.group.clients",
    items: [
      { path: "/leads", labelKey: "nav.leads", icon: UserPlus, perm: "manage:crm.clients", badge: "leads" },
      { path: "/clients", labelKey: "nav.clients", icon: Users, perm: "view:crm" },
      {
        path: "/sales",
        labelKey: "nav.sales",
        icon: ReceiptText,
        perm: "manage:crm.sales",
        badge: "salesOverdue",
        children: SALES_CHILDREN,
      },
      { path: "/expenses", labelKey: "nav.expenses", icon: Wallet, perm: "manage:crm.expenses" },
    ],
  },
  {
    titleKey: "nav.group.work",
    items: [
      { path: "/projects", labelKey: "nav.projects", icon: FolderKanban, perm: "manage:crm.projects" },
      { path: "/tasks", labelKey: "nav.tasks", icon: ListTodo, perm: "manage:crm.projects", badge: "tasksOpen" },
      { path: "/contracts", labelKey: "nav.contracts", icon: FileSignature, perm: "manage:crm.contracts" },
      { path: "/support", labelKey: "nav.support", icon: LifeBuoy, perm: "manage:crm.support", badge: "ticketsOpen" },
      // Customer site monitoring. Under Work beside support because it is the
      // same promise kept from the other end: a ticket is the customer telling
      // us something broke, this is us noticing before they do. `view:crm`,
      // not the write key: whether a customer's site is up is something anybody
      // on the desk may need to answer on the phone, and every change inside
      // the section answers to `manage:crm.monitoring` on its own. The badge
      // counts confirmed outages only - a single failed check is not news.
      { path: "/monitoring", labelKey: "nav.monitoring", icon: Activity, perm: "view:crm", badge: "sitesDown" },
      // The credential vault. Under Work rather than under Clients because what
      // is stored here is the machinery a project runs on - the hosting, the
      // database, the CMS login - and it is opened while doing the work, not
      // while reading the client record. Its own key (`read:crm.vault`), never
      // `view:crm`: a hostname beside a username is half a credential.
      { path: "/vault", labelKey: "nav.vault", icon: KeyRound, perm: "read:crm.vault" },
    ],
  },
  {
    // Reports and the operator's tools, between the work itself and the system
    // settings. Perfex keeps these as two menus of its own (Reports and
    // Utilities) right above Setup; ours is one heading because each side is
    // currently one destination, and a heading over a single pill costs more
    // chrome than it earns.
    titleKey: "nav.group.tools",
    items: [
      // `view:crm`, the same key every other CRM register reads on: the five
      // reports are sums over sales, expenses, leads and time, and each of the
      // routes behind them checks exactly that.
      { path: "/reports", labelKey: "nav.reports", icon: PieChart, perm: "view:crm" },
      // The SALES key, not `view:crm`, even though a single document's PDF opens
      // on the wider one. An export is a bigger read than a download - the same
      // argument the call log's CSV made - and the route audits it.
      { path: "/tools/bulk-pdf", labelKey: "nav.bulkPdf", icon: FileArchive, perm: "manage:crm.sales" },
    ],
  },
  {
    titleKey: "nav.group.system",
    items: [
      { path: "/plans", labelKey: "nav.plans", icon: Boxes, perm: "manage:plans" },
      { path: "/subscriptions", labelKey: "nav.subscriptions", icon: Layers, perm: "manage:plans" },
      { path: "/media", labelKey: "nav.media", icon: Images, perm: "manage:media" },
      { path: "/settings", labelKey: "nav.settings", icon: Settings2, perm: SETTINGS_PERMS },
    ],
  },
];

/**
 * The routed sections. `section` is the key CrmLayout looks the component up
 * by; `nested` says whether the section owns sub-routes and therefore needs the
 * `/*` suffix.
 */
export interface CrmRoute {
  path: string;
  perm: NavPerm;
  section: string;
  nested: boolean;
}

export const CRM_ROUTES: CrmRoute[] = [
  // One page, no sub-routes: the month is state, not a URL segment.
  { path: "/calendar", perm: "view:crm", section: "calendar", nested: false },
  // Nested: the index is the shelf list, `/guides/:slug` is one guide, and
  // `/guides/:slug/muuda` is the editor. A guide has to be linkable - it gets
  // pasted into tasks and ticket replies - so none of these are dialogs.
  { path: "/guides", perm: "view:crm", section: "guides", nested: true },
  { path: "/marketing", perm: "view:crm", section: "marketing", nested: true },
  { path: "/ariregister", perm: "view:ariregister", section: "ariregister", nested: true },
  // Nested: every tab is a segment under it, and the brain tab has one more
  // (`/prospects/brain/:domain`, one company, keyed by domain rather than
  // register code because a scan lead may have no register code at all).
  { path: PROSPECTS, perm: "view:ariregister", section: "prospects", nested: true },
  // The five retired addresses. Each keeps a route, under the SAME permission
  // it always had, and mounts the redirect onto its tab with the query string
  // and the encoded domain intact (`legacyTarget` in the prospecting folder).
  // Nested exactly where the old page was, so `/brain/<domain>` still arrives.
  { path: "/site-scanner", perm: "view:ariregister", section: "prospects-legacy", nested: true },
  { path: "/scan-leads", perm: "view:ariregister", section: "prospects-legacy", nested: true },
  { path: "/site-audits", perm: "view:ariregister", section: "prospects-legacy", nested: false },
  { path: "/brain", perm: "view:ariregister", section: "prospects-legacy", nested: true },
  { path: "/brain-due", perm: "view:ariregister", section: "prospects-legacy", nested: false },
  { path: "/email", perm: "view:crm", section: "email", nested: true },
  { path: "/calls", perm: CALLS_PERMS, section: "calls", nested: true },
  // Nested: the index is the results page, `/ai-calls/campaigns` the campaign
  // list and `/ai-calls/campaigns/:id` one campaign, linkable from a task.
  { path: "/ai-calls", perm: "use:crm.calls", section: "ai-calls", nested: true },
  { path: "/leads", perm: "manage:crm.clients", section: "leads", nested: true },
  { path: "/clients", perm: "view:crm", section: "clients", nested: true },
  { path: "/sales", perm: "manage:crm.sales", section: "sales", nested: true },
  { path: "/expenses", perm: "manage:crm.expenses", section: "expenses", nested: true },
  { path: "/projects", perm: "manage:crm.projects", section: "projects", nested: true },
  { path: "/tasks", perm: "manage:crm.projects", section: "tasks", nested: true },
  { path: "/contracts", perm: "manage:crm.contracts", section: "contracts", nested: true },
  { path: "/support", perm: "manage:crm.support", section: "support", nested: true },
  // Nested: the index is the desk and `/monitoring/:id` one site, linkable from
  // the outage alert and the client timeline.
  { path: "/monitoring", perm: "view:crm", section: "monitoring", nested: true },
  { path: "/vault", perm: "read:crm.vault", section: "vault", nested: false },
  // One page, five tabs. The tab is a query parameter rather than a segment, so
  // switching it cannot remount the section and lose the chosen date range.
  { path: "/reports", perm: "view:crm", section: "reports", nested: false },
  // Nested, though it holds one page today: the section exists to be where the
  // next operator tool goes, and `/tools` alone redirects into the first of them.
  { path: "/tools", perm: "manage:crm.sales", section: "tools", nested: true },
  { path: "/plans", perm: "manage:plans", section: "plans", nested: true },
  { path: "/subscriptions", perm: "manage:plans", section: "subscriptions", nested: true },
  { path: "/media", perm: "manage:media", section: "media", nested: false },
  // The SAME array instance as the nav item above, not a copy: `nav.test.ts`
  // asserts a link and its route agree on the permission, and two equal arrays
  // are not equal.
  { path: "/settings", perm: SETTINGS_PERMS, section: "settings", nested: true },
];

/**
 * True when `pathname` is the route itself or one of its sub-routes.
 *
 * The trailing slash matters: without it `/brain-due` would count as being
 * inside `/brain`, and a future `/marketing-x` as being inside `/marketing`.
 */
export function isRouteActive(pathname: string, routePath: string): boolean {
  return pathname === routePath || pathname.startsWith(`${routePath}/`);
}

/** True when the sidebar should highlight `item` for the current route. */
export function isNavActive(pathname: string, item: CrmNavItem): boolean {
  const own = item.exact ? pathname === item.path : isRouteActive(pathname, item.path);
  return own || (item.match ?? []).some((prefix) => isRouteActive(pathname, prefix));
}

/**
 * Pages that have NO sidebar row but must still be reachable by name.
 *
 * Automation and the opt-out list are sender-wide rather than campaign-wide and
 * both are opened rarely, so a permanent row only cost every reader of the
 * Marketing group a line to scan; the campaign list's head carries a button to
 * each instead. They stay in this file because the command palette indexes it,
 * and the palette is now the only place either page can be found by name. A
 * page nobody can search for is a page that exists for whoever remembers the
 * URL and for nobody else.
 *
 * The Kliendiotsing tabs are here under the names their pages had before they
 * became tabs. Whoever learned the CRM under those names will type "skanner"
 * or "vihjed" into the palette, and folding five rows into one must not quietly
 * make those words find nothing. The tab strip teaches the new names
 * ("Skannid", "Sissetulevad", "Rikked"), so each entry answers to its tab's
 * name as well (`aliasKeys`). The old name stays the one shown: in English the
 * call queue's tab is "Leads", and a hit reading just that would be two hits
 * with one name, the other being the Leads section. Rikked never was a page of
 * its own, so its tab name is its only name. (The follow-ups need no entry:
 * they kept their sidebar row, which the palette indexes anyway.)
 */
export const CRM_NAV_UNLISTED: CrmNavItem[] = [
  { path: "/marketing/automation", labelKey: "nav.marketingAutomation", icon: Timer, perm: "view:crm" },
  { path: "/marketing/suppression", labelKey: "nav.suppression", icon: Ban, perm: "view:crm" },
  { path: stepPath("inbound"), labelKey: "nav.siteAudits", aliasKeys: ["prospecting.tabs.inbound"], icon: ShieldCheck, perm: "view:ariregister" },
  { path: stepPath("scans"), labelKey: "nav.siteScanner", aliasKeys: ["prospecting.tabs.scans"], icon: Radar, perm: "view:ariregister" },
  { path: stepPath("leads"), labelKey: "nav.scanLeads", aliasKeys: ["prospecting.tabs.leads"], icon: Target, perm: "view:ariregister" },
  { path: stepPath("brain"), labelKey: "nav.brain", aliasKeys: ["prospecting.tabs.brain"], icon: Brain, perm: "view:ariregister" },
  { path: stepPath("incidents"), labelKey: "prospecting.tabs.incidents", icon: Siren, perm: "view:ariregister" },
];

/** Every nav item in one list - what the command palette indexes. Children come
 *  along: somebody searching for "krediitarved" means the page, not the section
 *  that happens to contain it. So do the rowless pages: the palette is the only
 *  place a page without a sidebar row can be found by name. */
export function flatNav(): CrmNavItem[] {
  return [
    ...CRM_NAV.flatMap((g) =>
      g.items.flatMap((item) => [
        item,
        ...(item.children ?? []).map((c) => ({ ...c, perm: item.perm })),
      ]),
    ),
    ...CRM_NAV_UNLISTED,
  ];
}
